// Promise-based alert/confirm dialogs.
//
// The React Native build used `Alert.alert`, which has no web equivalent
// (react-native-web does not implement it, and the browser's native
// window.confirm blocks the event loop and cannot be styled or translated).
// This provides the same call sites an async API instead:
//
//   await dialog.alert(title, body);
//   if (await dialog.confirm(title, body, { destructive: true })) { … }
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

interface ConfirmOptions {
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface DialogApi {
  alert: (title: string, body?: string, closeLabel?: string) => Promise<void>;
  confirm: (title: string, body: string, opts?: ConfirmOptions) => Promise<boolean>;
}

interface DialogState {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string | null; // null => alert (single button)
  destructive: boolean;
  resolve: (value: boolean) => void;
}

const Ctx = createContext<DialogApi | null>(null);

export function useDialog(): DialogApi {
  const api = useContext(Ctx);
  if (!api) throw new Error('useDialog must be used within <DialogProvider>');
  return api;
}

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<DialogState | null>(null);

  const alert = useCallback(
    (title: string, body = '', closeLabel = 'OK') =>
      new Promise<void>((resolve) => {
        setState({
          title,
          body,
          confirmLabel: closeLabel,
          cancelLabel: null,
          destructive: false,
          resolve: () => resolve(),
        });
      }),
    [],
  );

  const confirm = useCallback(
    (title: string, body: string, opts: ConfirmOptions = {}) =>
      new Promise<boolean>((resolve) => {
        setState({
          title,
          body,
          confirmLabel: opts.confirmLabel ?? 'OK',
          cancelLabel: opts.cancelLabel ?? 'Cancel',
          destructive: !!opts.destructive,
          resolve,
        });
      }),
    [],
  );

  const settle = useCallback(
    (value: boolean) => {
      state?.resolve(value);
      setState(null);
    },
    [state],
  );

  return (
    <Ctx.Provider value={{ alert, confirm }}>
      {children}
      {state && <DialogView state={state} onSettle={settle} />}
    </Ctx.Provider>
  );
}

function DialogView({ state, onSettle }: { state: DialogState; onSettle: (v: boolean) => void }) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onSettle(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onSettle]);

  return (
    <div
      className="backdrop backdrop--center"
      onClick={(e) => {
        // Only a click on the backdrop itself dismisses.
        if (e.target === e.currentTarget) onSettle(false);
      }}
    >
      <div className="dialog" role="alertdialog" aria-modal="true" aria-labelledby="dlg-title">
        <h2 className="dialog__title" id="dlg-title">
          {state.title}
        </h2>
        {!!state.body && <p className="dialog__body">{state.body}</p>}
        <div className="dialog__actions">
          {state.cancelLabel !== null && (
            <button type="button" className="btn btn--text" onClick={() => onSettle(false)}>
              {state.cancelLabel}
            </button>
          )}
          <button
            type="button"
            ref={confirmRef}
            className={`btn ${state.destructive ? 'btn--danger' : 'btn--primary'}`}
            onClick={() => onSettle(true)}
          >
            {state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
